import { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValue,
  SingleValue as SingleValueType,
} from 'react-select'
import { SelectInput, SelectOption } from '../SelectInput/SelectInput'
import { FlagEmoji } from '../FlagEmoji/FlagEmoji'
import { countriesList } from './countriesList'

import './CountrySelect.css'
import { getFlagEmoji } from '@/methods/flagEmoji'
import { sortByLabel } from '@/methods/sortCountries'
import { getLocalizedCountryLabel, localizeCountries } from '@/methods/localizeCountries'
import { StoreContext } from '@store/Root.store'

const { Option, SingleValue } = components

interface CountrySelectProps {
  value: null | SelectOption
  className?: string
  onChange: (value: SelectOption | null) => void
  autoFocus?: boolean
  defaultMenuIsOpen?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  externalList?: any[]
  placeholder?: string
  showCheckmark?: boolean
}

// Lists supply the flag emoji as `icon`; otherwise derive it from the alpha-2 `value`.
const getCountryFlag = (option: { value?: string; icon?: string }): string =>
  option.icon || getFlagEmoji(option.value)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CountrySingleValue = (props: any) => (
  <SingleValue {...props}>
    <div className="country-select__country-item-inner onboarding-country-select-item">
      <FlagEmoji className="country-select__country-flag" emoji={getCountryFlag(props.data)} />
      <span>{props.data.label}</span>
    </div>
  </SingleValue>
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CountryOption = (props: any) => (
  <Option {...props}>
    <div className="country-select__country-item-inner onboarding-country-select-item">
      <FlagEmoji className="country-select__country-flag" emoji={getCountryFlag(props.data)} />
      <span>{props.data.label}</span>
    </div>
  </Option>
)

const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, boolean, GroupBase<SelectOption>>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        aria-hidden="true"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.96 9.04036C18.2139 9.2942 18.2139 9.70575 17.96 9.95959L12.96 14.9596C12.7062 15.2134 12.2946 15.2134 12.0408 14.9596L7.04077 9.9596C6.78693 9.70575 6.78693 9.2942 7.04077 9.04036C7.29461 8.78652 7.70617 8.78652 7.96001 9.04036L12.5004 13.5807L17.0408 9.04036C17.2946 8.78652 17.7062 8.78652 17.96 9.04036Z"
          fill="black"
        />
      </svg>
    </components.DropdownIndicator>
  )
}

const ClearIndicator = (
  props: ClearIndicatorProps<SelectOption, boolean, GroupBase<SelectOption>>
) => {
  return (
    <components.ClearIndicator {...props}>
      <svg
        aria-hidden="true"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.54048 6.54036C6.79432 6.28652 7.20588 6.28652 7.45972 6.54036L12.0001 11.0807L16.5405 6.54036C16.7943 6.28652 17.2059 6.28652 17.4597 6.54036C17.7136 6.7942 17.7136 7.20575 17.4597 7.45959L12.9193 12L17.4597 16.5404C17.7136 16.7942 17.7136 17.2058 17.4597 17.4596C17.2059 17.7134 16.7943 17.7134 16.5405 17.4596L12.0001 12.9192L7.45972 17.4596C7.20588 17.7134 6.79432 17.7134 6.54048 17.4596C6.28664 17.2058 6.28664 16.7942 6.54048 16.5404L11.0809 12L6.54048 7.45959C6.28664 7.20575 6.28664 6.7942 6.54048 6.54036Z"
          fill="black"
        />
      </svg>
    </components.ClearIndicator>
  )
}

export const CountrySelect = observer((props: CountrySelectProps) => {
  const store = useContext(StoreContext)
  const { language, translations } = store.TranslationsState
  const [selectValue, setSelectValue] = useState<SelectOption | null>(null)

  const options = useMemo(() => {
    if (props.externalList) {
      return sortByLabel(props.externalList, language)
    }
    return sortByLabel(localizeCountries(countriesList, language), language)
  }, [props.externalList, language])

  function handleChange(value: MultiValue<SelectOption> | SingleValueType<SelectOption>) {
    const singleValue = value as SelectOption | null // CountrySelect is always single-select
    setSelectValue(singleValue)
    props.onChange(singleValue)
  }

  useEffect(() => {
    if (props.value && !props.externalList) {
      setSelectValue({
        ...props.value,
        label: getLocalizedCountryLabel(props.value.value, props.value.label, language),
      })
    } else {
      setSelectValue(props.value)
    }
  }, [props.value, props.externalList, language])

  return (
    <div className="country-select__container onboarding-country-select">
      <SelectInput
        options={options}
        value={selectValue}
        onChange={handleChange}
        isClearable={true}
        isSearchable={true}
        components={{
          SingleValue: CountrySingleValue,
          Option: CountryOption,
          DropdownIndicator,
          ClearIndicator,
        }}
        className={props.className ? props.className : ''}
        autoFocus={props.autoFocus ? props.autoFocus : false}
        defaultMenuIsOpen={props.defaultMenuIsOpen ? props.defaultMenuIsOpen : false}
        placeholder={
          props.placeholder ? props.placeholder : translations?.countrySelect?.placeholder
        }
        showCheckmark={props.showCheckmark}
      />
    </div>
  )
})
